import { requestBlob } from '@myua/api';
import {
  assetOriginalUrl,
  isOriginalImageFormat,
  MagicPrimaryAsset,
  MagicRef,
  MAX_ORIGINAL_BYTES,
  MAX_REFS,
  toAppApiUrl,
} from './model';
import { blobImageMime } from './imageFormat';
import { fitInlineBlobs } from './refCompression';
import { MagicImage } from './providers/types';
import type { AiReference } from './aiApi';

// Turns the studio session's references (MagicRef[]) into provider-ready base64
// images. This is the fix for "asset images never reach the model":
//
//   • Uploaded / pasted refs are blob: object URLs — fetched directly, which
//     already worked.
//   • Asset refs point at thumbnail_url / preview.url / download_url, which can
//     be cross-origin signed URLs the browser renders in <img> but cannot read
//     bytes from via a plain fetch() (CORS / no credentials). We pull them
//     through @myua/api's requestBlob, which resolves relative paths against
//     our origin and sends credentials, so any same-origin authenticated source
//     yields bytes; cross-origin candidates simply fail and we fall through to
//     the next one.
//
// Resolution happens in two steps: every reference is first reduced to bytes,
// then — on the backend path only — the references that must travel inline are
// fitted into the request body budget (refCompression) before being
// base64-encoded. Fitting has to see all of them at once, since the budget
// belongs to the request rather than to any single reference, which is why base64
// is the last step instead of something each reference does for itself.
//
// The direct path deliberately skips fitting: it posts to Google's endpoint, not
// through our ingress, and already has the Files API for oversize references
// (providers/filesApi.ts). Re-encoding there would cost quality for nothing.
//
// Providers receive only the resolved base64 — they never touch URLs, so this
// resolution is shared across every provider (Gemini, backend, fake).

function base64FromBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the "data:<mime>;base64," prefix — providers want raw base64.
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}

// One inline reference as the backend receives it. `image_base64` accepts either
// bare base64 or a full data URL (see AiReference in the service's schema:
// "image_base64 — base64 или data URL"), and we send the data URL, because its
// prefix is the only place the reference's format is stated. Stripped, the proxy
// has to guess the format before handing the bytes to OpenAI / Gemini, and a
// guess of PNG is wrong for exactly the references that aren't one: a reused
// Nano Banana result (JPEG) and anything refCompression re-encoded (WebP).
//
// The mime is read from the bytes rather than from Blob.type, which is empty or
// application/octet-stream for anything pulled from a storage URL. When even the
// bytes don't say, bare base64 goes out as before — better to leave the backend
// to its own detection than to assert a format we don't know.
async function toInlineBase64(blob: Blob): Promise<string> {
  const [mime, base64] = await Promise.all([blobImageMime(blob), base64FromBlob(blob)]);
  return mime?.startsWith('image/') ? `data:${mime};base64,${base64}` : base64;
}

// A blob is usable as an image reference if it carries bytes and is an image, or
// if the server did not commit to a type (empty / octet-stream) — common for
// storage URLs.
function isUsableImageBlob(blob: Blob): boolean {
  if (blob.size === 0) return false;
  const t = blob.type;
  return t === '' || t === 'application/octet-stream' || t.startsWith('image/');
}

function pickMime(blob: Blob, name?: string): string {
  const t = blob.type;
  // Skip vendor types Gemini can't read (e.g. PSD) — fall back to extension.
  if (t.startsWith('image/') && t !== 'image/vnd.adobe.photoshop') return t;
  const ext = name && name.includes('.') ? name.split('.').pop()?.toLowerCase() : '';
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'webp':
      return 'image/webp';
    case 'gif':
      return 'image/gif';
    default:
      return 'image/png';
  }
}

// Byte sources for an asset, best first, plus whether a present original was
// dropped for being too large. When the original is usable (see assetOriginalUrl
// in model.ts — supported format, downloadable, within the size cap) we lead
// with it; otherwise the preview render (a flattened image even for non-raster
// originals like PSD) leads instead. Thumbnail is the last fallback.
function assetImageCandidates(asset: MagicPrimaryAsset): {
  urls: string[];
  oversize: boolean;
} {
  const original = assetOriginalUrl(asset); // null when missing, unsupported or oversize
  const preview = asset.preview?.url ?? null;
  const thumbnail = asset.thumbnail_url ?? null;

  const ordered = original ? [original, preview, thumbnail] : [preview, thumbnail];
  const urls = Array.from(new Set(ordered.filter((u): u is string => !!u).map(toAppApiUrl)));
  // Warn only when a usable-format original was set aside purely for its size
  // and a preview/thumbnail survived to replace it. Unsupported formats
  // (PSD/GIF/video/…) use the preview by design — no warning — and assets with
  // no download link aren't oversize.
  const oversize =
    isOriginalImageFormat(asset) &&
    !!asset.download_url &&
    asset.size > MAX_ORIGINAL_BYTES &&
    urls.length > 0;
  return { urls, oversize };
}

// Bookkeeping carried alongside a reference's bytes: the display name (used for
// the mime fallback and for warning the user by name), the source asset id (for
// deduping and for the backend's `{ asset_id }` shortcut) and, for asset refs,
// the asset name when its original was too large and we substituted the preview
// render.
type RefMeta = {
  name: string;
  assetId: number | null;
  oversizeName?: string;
};

// Outcome of reducing one reference to bytes; `blob` is null when every
// candidate source failed.
type RefBytes = RefMeta & { blob: Blob | null };
type LoadedRef = RefMeta & { blob: Blob };

async function resolveUploadBytes(url: string, name: string): Promise<RefBytes> {
  const meta: RefMeta = { name, assetId: null };
  try {
    const res = await fetch(url);
    if (!res.ok) return { ...meta, blob: null };
    const blob = await res.blob();
    return { ...meta, blob: isUsableImageBlob(blob) ? blob : null };
  } catch {
    return { ...meta, blob: null };
  }
}

async function resolveAssetBytes(asset: MagicPrimaryAsset): Promise<RefBytes> {
  const { urls, oversize } = assetImageCandidates(asset);
  const meta: RefMeta = { name: asset.name, assetId: asset.id };
  for (const url of urls) {
    const out = await requestBlob({ url });
    if (out.success && isUsableImageBlob(out.data.blob)) {
      return {
        ...meta,
        blob: out.data.blob,
        oversizeName: oversize ? asset.name : undefined,
      };
    }
  }
  return { ...meta, blob: null };
}

function resolveRefBytes(ref: MagicRef): Promise<RefBytes> {
  return ref.kind === 'upload'
    ? resolveUploadBytes(ref.url, ref.name)
    : resolveAssetBytes(ref.asset);
}

async function toImage(blob: Blob, meta: RefMeta): Promise<MagicImage> {
  return {
    // Read from the blob we are actually sending, so a re-encoded reference is
    // announced as the format it now is (image/webp) rather than its original.
    mimeType: pickMime(blob, meta.name),
    dataBase64: await base64FromBlob(blob),
    name: meta.name,
    assetId: meta.assetId,
    bytes: blob.size,
  };
}

export type ResolvedRefs = {
  images: MagicImage[];
  // Number of references whose bytes could not be obtained (every candidate
  // source failed) — lets the caller warn instead of silently dropping refs.
  failed: number;
  // Names of asset references whose original exceeded the inline-size limit, so
  // the lower-resolution preview was sent instead. Lets the caller warn.
  oversize: string[];
};

export async function resolveRefs(refs: MagicRef[]): Promise<ResolvedRefs> {
  const resolved = await Promise.all(refs.slice(0, MAX_REFS).map(resolveRefBytes));

  const loaded: LoadedRef[] = [];
  const seenAssetIds = new Set<number>();
  let failed = 0;
  for (const ref of resolved) {
    if (!ref.blob) {
      failed += 1;
      continue;
    }
    // Dedupe the same asset added more than once; keep every upload.
    if (ref.assetId != null) {
      if (seenAssetIds.has(ref.assetId)) continue;
      seenAssetIds.add(ref.assetId);
    }
    loaded.push({ ...ref, blob: ref.blob });
  }

  // No body budget on this path (see the header) — the original bytes go out.
  const images = await Promise.all(loaded.map((ref) => toImage(ref.blob, ref)));

  return {
    images,
    failed,
    oversize: loaded
      .map((ref) => ref.oversizeName)
      .filter((name): name is string => name !== undefined),
  };
}

// ─── Backend path ────────────────────────────────────────────────────
// The proxy (POST /ai/generations/) resolves Asset Lab assets by id itself, so
// for a plain image asset we can skip downloading and re-uploading its bytes
// entirely and just send `{ asset_id }`. Everything the backend can't read that
// way still goes as inline base64:
//
//   • uploads / clipboard pastes — no asset id exists;
//   • non-raster originals (PSD, GIF, video / 3D frames) — the backend answers
//     400 "Asset N is not an image"; we already substitute a flattened preview
//     render locally, so we send those pixels instead;
//   • originals over MAX_ORIGINAL_BYTES — the backend caps reference images at
//     5 MB; the preview render we substitute is what gets sent, and the asset's
//     name is reported back so the modal can warn (same warning as before).
//
// Only that inline group is charged against the request body budget — an
// `{ asset_id }` reference costs a handful of bytes no matter how large the
// asset is, which is also why it is worth keeping the shortcut.

export type ResolvedBackendRefs = {
  references: AiReference[];
  // References whose bytes could not be obtained at all (every source failed).
  failed: number;
  // Names of assets sent as a lower-resolution preview because the original
  // exceeded the size cap.
  oversize: string[];
  // Names of references re-encoded smaller to fit the request body budget.
  compressed: string[];
};

// True when the asset can be handed to the backend by id: a raster format the
// backend will accept, small enough for its reference-size cap, and actually
// downloadable server-side.
function canSendByAssetId(asset: MagicPrimaryAsset): boolean {
  return isOriginalImageFormat(asset) && !!asset.download_url && asset.size <= MAX_ORIGINAL_BYTES;
}

// How one surviving reference will travel. 'bytes' entries share the body
// budget; 'id' entries don't touch it.
type BackendSlot = { kind: 'id'; assetId: number } | { kind: 'bytes'; loaded: LoadedRef };

export async function resolveBackendRefs(refs: MagicRef[]): Promise<ResolvedBackendRefs> {
  const capped = refs.slice(0, MAX_REFS);

  const slots = await Promise.all(
    capped.map(async (ref): Promise<BackendSlot | null> => {
      if (ref.kind === 'asset' && canSendByAssetId(ref.asset)) {
        return { kind: 'id', assetId: ref.asset.id };
      }
      const bytes = await resolveRefBytes(ref);
      return bytes.blob ? { kind: 'bytes', loaded: { ...bytes, blob: bytes.blob } } : null;
    }),
  );

  const kept: BackendSlot[] = [];
  const seenAssetIds = new Set<number>();
  let failed = 0;

  capped.forEach((ref, i) => {
    const slot = slots[i];
    if (!slot) {
      failed += 1;
      return;
    }
    // Dedupe the same asset added more than once (by asset id, regardless of
    // whether it travels as an id or as bytes); keep every upload.
    const assetId = ref.kind === 'asset' ? ref.asset.id : null;
    if (assetId != null) {
      if (seenAssetIds.has(assetId)) return;
      seenAssetIds.add(assetId);
    }
    kept.push(slot);
  });

  const inline = kept.flatMap((slot) => (slot.kind === 'bytes' ? [slot.loaded] : []));
  const { blobs, compressed } = await fitInlineBlobs(
    inline.map(({ blob, name }) => ({ blob, name })),
  );
  const encoded = await Promise.all(blobs.map(toInlineBase64));

  const references: AiReference[] = [];
  const oversize: string[] = [];
  let inlineIndex = 0;

  for (const slot of kept) {
    if (slot.kind === 'id') {
      references.push({ asset_id: slot.assetId });
      continue;
    }
    references.push({ image_base64: encoded[inlineIndex] });
    if (slot.loaded.oversizeName) oversize.push(slot.loaded.oversizeName);
    inlineIndex += 1;
  }

  return { references, failed, oversize, compressed };
}
